import { Slack } from 'lucide-react';

import { PortalEmptyState } from '@/modules/hiring-manager/components/portalEmptyState/PortalEmptyState';
import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { useSlackCallbackParams } from '@/modules/integrations/hooks/useSlackCallbackParams';
import { slackAppRedirectUrl } from '@/modules/integrations/slackAppRedirectUrl';

const integrationsAction = {
  label: 'Back to Integrations',
  to: '/hm/integrations',
};

/**
 * Slack OAuth result for a portal user who connected from Integrations
 * (`connectSource: 'App'`). The API redirects to `/slack-connect/:status`;
 * `ProtectedLayout` rewrites it here for hiring managers, and recruiters
 * stay on `RecruiterSlackConnectResult`. Integrations opens OAuth in a new
 * tab, so this usually renders there; "Try again" stays in this tab.
 */
export const HMSlackConnectResult = () => {
  const { status, appId, teamId, isCancelled } = useSlackCallbackParams();
  const connectSlack = useConnectSlack();

  if (status === 'success') {
    return (
      <PortalEmptyState
        className="mx-auto max-w-lg"
        icon={Slack}
        title="Slack connected"
        description="Chat with Finlay in Slack to create jobs, review shortlists and approve or reject candidates. Finlay posts updates there too. You can close this tab and go back to Finlay."
        action={
          appId
            ? {
                label: 'Open Slack',
                href: slackAppRedirectUrl({ appId, teamId }),
              }
            : integrationsAction
        }
        secondaryAction={appId ? integrationsAction : undefined}
      />
    );
  }

  return (
    <PortalEmptyState
      className="mx-auto max-w-lg"
      icon={Slack}
      title={isCancelled ? 'Slack isn’t connected' : 'Slack connection failed'}
      description={
        isCancelled
          ? 'You stopped before Slack finished connecting. Try again whenever you’re ready.'
          : 'We could not finish connecting Slack. Try again, or connect it later from Integrations.'
      }
      action={{ label: 'Try again', onClick: () => connectSlack('App') }}
      secondaryAction={integrationsAction}
    />
  );
};
